import { useEffect, useState } from 'react'
import { RenderFieldExtensionCtx } from 'datocms-plugin-sdk'
import Cell, { initialCellValue } from 'src/components/Cell'
import s from './styles.module.css'
import { Cell as CellType, Table as TableType, TableData, TableOptions } from 'src/types'
import { isEqual } from 'lodash'
import { FaMinusCircle } from 'react-icons/fa'

type OwnProps = {
  ctx: RenderFieldExtensionCtx
  value: TableType
  onUpdate: (value: TableType) => void
}

export default function Table(props: OwnProps) {
  const [title, setTitle] = useState<string>(props.value.title ?? '')
  const [options, setOptions] = useState<TableOptions>(props.value.options)
  const [data, setData] = useState<TableData>(props.value.data)
  // Because the table is a 2D array, we need to force an update when the table is updated
  // Until we find a better solution to deep compare the 2D array
  const [forceUpdate, setForceUpdate] = useState(false)

  useEffect(() => {
    const value = { title, options, data }
    if (!isEqual(value, props.value) || forceUpdate) {
      props.onUpdate(value)
      if (forceUpdate) setForceUpdate(false)
    }
  }, [props, title, options, data, forceUpdate])

  const onUpdate = (rowIndex: number, columnIndex: number, cellData: CellType) => {
    const rows = [...data]
    rows[rowIndex][columnIndex] = cellData
    setData(rows)
    setForceUpdate(true)
  }

  const onAddColumn = (columnIndex: number) => {
    setData(data.map((row) => {
      row.splice(columnIndex + 1, 0, initialCellValue())
      return row
    }))

    if (options.mainColumn !== -1 && columnIndex < options.mainColumn) setOptions({ ...options, mainColumn: options.mainColumn + 1 })
    setForceUpdate(true)
  }

  const onAddRow = (rowIndex: number) => {
    const rows = [...data]
    rows.splice(rowIndex + 1, 0, Array(data[0].length).fill(initialCellValue()))
    setData(rows)

    if (options.mainRow !== -1 && rowIndex < options.mainRow) setOptions({ ...options, mainRow: options.mainRow + 1 })
    setForceUpdate(true)
  }

  const onRemoveColumn = (columnIndex: number) => {
    setData(data.map((row) => {
      row.splice(columnIndex, 1)
      return row
    }))

    if (options.mainColumn !== -1 && columnIndex < options.mainColumn) setOptions({ ...options, mainColumn: options.mainColumn - 1 })
    setForceUpdate(true)
  }

  const onRemoveRow = (rowIndex: number) => {
    const rows = [...data]
    rows.splice(rowIndex, 1)
    setData(rows)

    if (options.mainRow !== -1 && rowIndex < options.mainRow) setOptions({ ...options, mainRow: options.mainRow - 1 })
    setForceUpdate(true)
  }

  return (
    <div className={s.container}>
      <input type="text" className={s.title} placeholder="Title" value={title} onChange={(e) => setTitle(e.target.value)} />
      <table className={s.table}>
        <tbody>
          {data.map((row, i) => (<tr key={i}>
            {data.length > 1 && <FaMinusCircle className={s.removeRow} onClick={() => onRemoveRow(i)} />}
            {row.map((value, j) => {
              if (i === 0) return (<th key={j}>
                <Cell
                  key={value.id}
                  ctx={props.ctx}
                  options={options}
                  value={value}
                  columnIndex={j}
                  rowIndex={i}
                  isTopRow={row.length > 1}
                  onUpdate={(e) => onUpdate(i, j, e)}
                  onUpdateOptions={(e) => setOptions(e)}
                  onAddColumn={() => onAddColumn(j)}
                  onAddRow={() => onAddRow(i)}
                  onRemoveColumn={() => onRemoveColumn(j)}
                />
              </th>)
              return (<td key={j}>
                <Cell
                  key={value.id}
                  ctx={props.ctx}
                  options={options}
                  value={value}
                  columnIndex={j}
                  rowIndex={i}
                  onUpdate={(e) => onUpdate(i, j, e)}
                  onUpdateOptions={(e) => setOptions(e)}
                  onAddColumn={() => onAddColumn(j)}
                  onAddRow={() => onAddRow(i)}
                  onRemoveColumn={() => onRemoveColumn(j)}
                />
              </td>)
            })}
          </tr>))}
        </tbody>
      </table>
    </div>
  )
}
